import { buildPromotionUrl } from '../lib/public-url';
import { isPromotionEventType, isPromotionTargetType } from '../lib/promotion-links';
import type { SupabaseClient } from '../supabase/client';
import type { Database } from '../supabase/database.types';
import type {
  BuildPromotionShareMessageInput,
  GetOrCreatePromotionLinkInput,
  PromotionAnalyticsEventCount,
  PromotionAnalyticsRow,
  PromotionAnalyticsSummary,
  PromotionEventType,
  PromotionLink,
  PromotionLinkWithUrl,
  PromotionTarget,
  PromotionTargetGroup,
  PromotionTargetQuery,
  PromotionTargetType,
  PromotionTargetsPage,
  RecordPromotionEventInput,
} from '../types/promotion-kit';

const PRODUCT_IMAGES_BUCKET = 'product-images';
const CATEGORY_MEDIA_BUCKET = 'category-media';
const STORE_LOGOS_BUCKET = 'store-logos';
const TARGET_TYPES: readonly PromotionTargetType[] = [
  'store_home',
  'product',
  'category',
  'live_offer_campaign',
  'sales_page',
  'review_highlight',
];
const EVENT_TYPES: readonly PromotionEventType[] = [
  'link_visit',
  'product_detail_view',
  'contact_click',
  'add_to_cart_click',
  'order_start_click',
];

export interface PromotionKitService {
  listPromotionTargets(
    client: SupabaseClient,
    args: PromotionTargetQuery & { readonly supabaseUrl: string },
  ): Promise<PromotionTargetsPage>;
  getOrCreatePromotionLink(
    client: SupabaseClient,
    input: GetOrCreatePromotionLinkInput,
  ): Promise<PromotionLinkWithUrl>;
  listPromotionAnalytics(
    client: SupabaseClient,
    args: { readonly storeId: string; readonly since?: string | null },
  ): Promise<PromotionAnalyticsSummary>;
  recordPromotionEvent(client: SupabaseClient, input: RecordPromotionEventInput): Promise<void>;
  buildPromotionShareMessage(input: BuildPromotionShareMessageInput): string;
}

type TargetPayload = Record<string, unknown>;
type QueryResponse<T> = {
  readonly data: T | null;
  readonly error: unknown | null;
};
/**
 * The name is checked against the generated functions. The arguments stay
 * untyped: the generated `Args` mark `target_id` non-null, but the RPC
 * accepts NULL for a store-level link.
 */
type RpcInvoker = (
  fn: keyof Database['public']['Functions'],
  args: Record<string, unknown>,
) => PromiseLike<QueryResponse<unknown>>;

/** The runtime shape of `SupabaseClient.rpc`, without its generic signature. */
interface UntypedRpcClient {
  rpc(fn: string, args: Record<string, unknown>): PromiseLike<QueryResponse<unknown>>;
}
type TargetsRpcPayload = {
  readonly store?: {
    readonly storeId?: unknown;
    readonly storeSlug?: unknown;
    readonly storeName?: unknown;
    readonly storeLogoPath?: unknown;
    readonly storePublished?: unknown;
  };
  readonly groups?: Record<string, { totalCount?: unknown; hasMore?: unknown; targets?: unknown }>;
};

/**
 * Never instantiates supabase-js's generic `rpc` signature, which is resolved
 * over every generated function: `client.rpc.bind(client)` did, and that was
 * TS2589 ("excessively deep") once the generated map grew. The call keeps
 * `client` as `this`.
 */
function rpc(client: SupabaseClient): RpcInvoker {
  const untyped = client as unknown as UntypedRpcClient;
  return (fn, args) => untyped.rpc(fn, args);
}

function storagePublicUrl(client: SupabaseClient, bucket: string, path: unknown): string | null {
  if (typeof path !== 'string' || !path.trim()) return null;
  const { data } = client.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}

function bucketForTarget(type: PromotionTargetType): string {
  if (type === 'category') return CATEGORY_MEDIA_BUCKET;
  if (type === 'store_home') return STORE_LOGOS_BUCKET;
  return PRODUCT_IMAGES_BUCKET;
}

function toTarget(
  client: SupabaseClient,
  storeId: string,
  type: PromotionTargetType,
  value: TargetPayload,
): PromotionTarget | null {
  const targetId = typeof value.targetId === 'string' ? value.targetId : null;
  const title = typeof value.title === 'string' ? value.title : '';
  const publicPath = typeof value.publicPath === 'string' ? value.publicPath : '';
  if (!title || !publicPath.startsWith('/s/')) return null;
  const base = {
    type,
    id: `${type}:${targetId ?? 'none'}`,
    storeId,
    targetId,
    title,
    subtitle: typeof value.subtitle === 'string' ? value.subtitle : null,
    publicPath,
    imageUrl: storagePublicUrl(client, bucketForTarget(type), value.imagePath),
  };

  if (type === 'product' && targetId) {
    return {
      ...base,
      type,
      targetId,
      price_cents: typeof value.price_cents === 'number' ? value.price_cents : 0,
      sale_price_cents: typeof value.sale_price_cents === 'number' ? value.sale_price_cents : null,
      currency: 'ILS',
      available: typeof value.available === 'number' ? value.available : 0,
    };
  }
  if (type === 'category' && targetId) {
    return {
      ...base,
      type,
      targetId,
      productCount: typeof value.productCount === 'number' ? value.productCount : 0,
    };
  }
  if (type === 'live_offer_campaign' && targetId) {
    return {
      ...base,
      type,
      targetId,
      campaignSlug: typeof value.campaignSlug === 'string' ? value.campaignSlug : '',
      endsAt: typeof value.endsAt === 'string' ? value.endsAt : '',
      productCount: typeof value.productCount === 'number' ? value.productCount : 0,
    };
  }
  if (type === 'review_highlight' && targetId && typeof value.productId === 'string') {
    return {
      ...base,
      type,
      targetId,
      productId: value.productId,
      rating: typeof value.rating === 'number' ? value.rating : 0,
      reviewText: typeof value.reviewText === 'string' ? value.reviewText : '',
      customerDisplayName:
        typeof value.customerDisplayName === 'string' ? value.customerDisplayName : null,
    };
  }
  if (type === 'sales_page') {
    return {
      ...base,
      type,
      targetId: null,
      productCount: typeof value.productCount === 'number' ? value.productCount : 0,
    };
  }
  if (type === 'store_home') return { ...base, type, targetId: null };
  return null;
}

function groupLabelKey(type: PromotionTargetType): PromotionTargetGroup['labelKey'] {
  const keys: Record<PromotionTargetType, PromotionTargetGroup['labelKey']> = {
    store_home: 'admin.promotionKit.target.groups.storeHome',
    product: 'admin.promotionKit.target.groups.product',
    category: 'admin.promotionKit.target.groups.category',
    live_offer_campaign: 'admin.promotionKit.target.groups.liveOfferCampaign',
    sales_page: 'admin.promotionKit.target.groups.salesPage',
    review_highlight: 'admin.promotionKit.target.groups.reviewHighlight',
  };
  return keys[type];
}

function emptyKey(type: PromotionTargetType): PromotionTargetGroup['emptyKey'] {
  if (type === 'live_offer_campaign') return 'admin.promotionKit.empty.noLiveOffers';
  if (type === 'review_highlight') return 'admin.promotionKit.empty.noReviews';
  return null;
}

export async function listPromotionTargets(
  client: SupabaseClient,
  args: PromotionTargetQuery & { readonly supabaseUrl: string },
): Promise<PromotionTargetsPage> {
  void args.supabaseUrl;
  const { data, error } = await client.rpc('list_admin_promotion_targets', {
    target_store_id: args.storeId,
    target_type: args.targetType ?? undefined,
    search_term: args.search?.trim() || undefined,
    page_limit: args.limit ?? 25,
    page_offset: args.offset ?? 0,
  });
  if (error) throw error;
  if (!data || typeof data !== 'object') throw new Error('Promotion targets unavailable');

  const payload = data as TargetsRpcPayload;
  const store = payload.store;
  if (
    !store ||
    typeof store.storeId !== 'string' ||
    typeof store.storeSlug !== 'string' ||
    typeof store.storeName !== 'string'
  ) {
    throw new Error('Promotion targets missing store context');
  }

  const groups = TARGET_TYPES.flatMap((type) => {
    if (args.targetType && args.targetType !== type) return [];
    const raw = payload.groups?.[type];
    const targets = Array.isArray(raw?.targets)
      ? raw.targets.flatMap((item) =>
          item && typeof item === 'object'
            ? (toTarget(client, store.storeId as string, type, item as TargetPayload) ?? [])
            : [],
        )
      : [];
    return {
      type,
      labelKey: groupLabelKey(type),
      targets,
      totalCount: typeof raw?.totalCount === 'number' ? raw.totalCount : targets.length,
      hasMore: raw?.hasMore === true,
      emptyKey: emptyKey(type),
    };
  });

  return {
    store: {
      storeId: store.storeId,
      storeSlug: store.storeSlug,
      storeName: store.storeName,
      storeLogoUrl: storagePublicUrl(client, STORE_LOGOS_BUCKET, store.storeLogoPath),
      storePublished: store.storePublished === true,
    },
    groups,
    hasAnyAvailableTarget: groups.some((group) => group.targets.length > 0),
  };
}

export async function getOrCreatePromotionLink(
  client: SupabaseClient,
  input: GetOrCreatePromotionLinkInput,
): Promise<PromotionLinkWithUrl> {
  const { data, error } = await rpc(client)('get_or_create_promotion_link', {
    target_store_id: input.storeId,
    target_type: input.target.type,
    target_id: input.target.targetId ?? null,
    target_path: input.target.publicPath,
  });
  if (error) throw error;
  const row = data as Partial<PromotionLink> | null;
  const targetType = row?.target_type;
  if (!row || !targetType || !isPromotionTargetType(targetType)) {
    throw new Error('Promotion link RPC returned an invalid row');
  }
  const link: PromotionLink = {
    id: row.id ?? '',
    store_id: row.store_id ?? '',
    target_type: targetType,
    target_id: row.target_id ?? null,
    target_path: row.target_path ?? '',
    public_token: row.public_token ?? '',
    created_by: row.created_by ?? '',
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
    deleted_at: row.deleted_at ?? null,
  };
  return {
    ...link,
    publicUrl: buildPromotionUrl({
      publicStorefrontOrigin: input.publicUrlConfig.publicStorefrontOrigin,
      publicPath: link.target_path,
      token: link.public_token,
    }),
  };
}

export async function listPromotionAnalytics(
  client: SupabaseClient,
  args: { readonly storeId: string; readonly since?: string | null },
): Promise<PromotionAnalyticsSummary> {
  const { data, error } = await client.rpc('list_admin_promotion_analytics', {
    target_store_id: args.storeId,
    since: args.since ?? undefined,
  });
  if (error) throw error;

  const byLink = new Map<string, PromotionAnalyticsRow>();
  const totals = new Map<PromotionEventType, PromotionAnalyticsEventCount>();

  for (const row of data ?? []) {
    if (!isPromotionTargetType(row.target_type) || !isPromotionEventType(row.event_type)) continue;
    const count: PromotionAnalyticsEventCount = {
      eventType: row.event_type,
      count: row.event_count,
      countOverflowed: row.count_overflowed,
      lastEventAt: row.last_event_at,
    };
    const existing = byLink.get(row.link_id);
    if (existing) {
      byLink.set(row.link_id, { ...existing, counts: [...existing.counts, count] });
    } else {
      byLink.set(row.link_id, {
        linkId: row.link_id,
        publicToken: row.public_token,
        targetType: row.target_type,
        targetId: row.target_id,
        targetPath: row.target_path,
        counts: [count],
      });
    }

    const total = totals.get(row.event_type);
    totals.set(row.event_type, {
      eventType: row.event_type,
      count: (BigInt(total?.count ?? '0') + BigInt(row.event_count)).toString(),
      countOverflowed: (total?.countOverflowed ?? false) || row.count_overflowed,
      lastEventAt:
        total?.lastEventAt && row.last_event_at
          ? total.lastEventAt > row.last_event_at
            ? total.lastEventAt
            : row.last_event_at
          : (total?.lastEventAt ?? row.last_event_at),
    });
  }

  return {
    rows: [...byLink.values()],
    totals: EVENT_TYPES.map(
      (eventType) =>
        totals.get(eventType) ?? {
          eventType,
          count: '0',
          countOverflowed: false,
          lastEventAt: null,
        },
    ),
  };
}

export async function recordPromotionEvent(
  client: SupabaseClient,
  input: RecordPromotionEventInput,
): Promise<void> {
  const { error } = await client.rpc('record_promotion_event', {
    input_public_token: input.token,
    input_event_type: input.eventType,
    input_locale: input.locale ?? undefined,
  });
  if (error) throw error;
}

function shareKey(
  targetType: PromotionTargetType,
): Parameters<BuildPromotionShareMessageInput['t']>[0] {
  const keys: Record<PromotionTargetType, Parameters<BuildPromotionShareMessageInput['t']>[0]> = {
    store_home: 'promotionKit.whatsapp.storeHome',
    product: 'promotionKit.whatsapp.product',
    category: 'promotionKit.whatsapp.category',
    live_offer_campaign: 'promotionKit.whatsapp.liveOfferCampaign',
    sales_page: 'promotionKit.whatsapp.salesPage',
    review_highlight: 'promotionKit.whatsapp.reviewHighlight',
  };
  return keys[targetType];
}

export function buildPromotionShareMessage(input: BuildPromotionShareMessageInput): string {
  return input.t(shareKey(input.target.type), {
    storeName: input.storeName,
    targetName: input.target.title,
    url: input.publicUrl,
    lng: input.locale,
    interpolation: { escapeValue: false },
  });
}

export const promotionKitService: PromotionKitService = {
  listPromotionTargets,
  getOrCreatePromotionLink,
  listPromotionAnalytics,
  recordPromotionEvent,
  buildPromotionShareMessage,
};

export function mapPromotionKitError(
  err: unknown,
):
  | 'admin.promotionKit.errors.permissionDenied'
  | 'admin.promotionKit.errors.targetUnavailable'
  | 'admin.promotionKit.errors.linkUnavailable' {
  if (typeof err === 'object' && err !== null) {
    const code = (err as { readonly code?: unknown }).code;
    if (code === '42501') return 'admin.promotionKit.errors.permissionDenied';
    if (code === 'P0601') return 'admin.promotionKit.errors.targetUnavailable';
    if (code === 'P0591') return 'admin.promotionKit.errors.targetUnavailable';
  }
  return 'admin.promotionKit.errors.linkUnavailable';
}
